import { ThemeProvider, THEME_ID, createTheme } from "@mui/material/styles";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-quill/dist/quill.snow.css";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./layouts/Layout";
import RequireAuth from "./RequireAuth";
import LoginPage from "./pages/LoginPage";
import useAuth from "./hooks/useAuth";
import routes from "./routes.js";
import NoUserPage from "./pages/NoUserPage";
import FlightboardPage from "./pages/FlightboardPage";
import SocialCallbackPage from "./pages/SocialCallbackPage";
import GoogleMapsProvider from "./components/GoogleMapsProvider.js";
import ForgotPasswordPage from "./pages/ForgotPasswordPages/ForgotPasswordPage.js";
import ResetPasswordPage from "./pages/ForgotPasswordPages/ResetPasswordPage.js";
import OtpPage from "./pages/OtpPage.js";
import GetStartedPage from "./pages/GetStartedPage.js";
import TechnicianSignupForm from "./views/Signup/TechnicianSignupForm.js";
import CompanySignupForm from "./views/Signup/CompanySignupForm.js";
import CustomerSignupForm from "./views/Signup/CustomerSignupForm.js";
import { useEffect } from "react";
import { initAudio } from "./utils/playSound.js";
import CreateNewPasswordPage from "./pages/ForgotPasswordPages/CreateNewPasswordPage.js";

function App() {
  const { userModules, userPermissions, authRole, auth, isAuthenticated } = useAuth();
  const materialTheme = createTheme({
    palette: {
      primary: {
        // main: "#012169",
        main: "#0F40AE",
      },
      secondary: {
        main: "#FF5F00",
      },
      info: {
        main: "#FFFFFF",
      },
    },
    typography: {
      allVariants: {
        fontFamily: ["Poppins", "ui-sans-serif", "system-ui"],
      },
      button: {
        fontFamily: ["Poppins", "ui-sans-serif", "system-ui"],
        textTransform: "none",
        fontSize: "18px",
      },
      textField: {
        color: "blue",
      },
    },

    components: {
      MuiFormLabel: {
        root: {
          color: "#2EFF22",
        },
        styleOverrides: {
          asterisk: {
            color: "#FF5F00",
            fontSize: "22px",
          },
        },
      },
    },
  });

  useEffect(() => {
    initAudio();
  }, []);

  /* const filteredRoutes = routes.filter((route) => {
    // Allow routes with empty module (full access)
    if (route.module === "") {
      if (
        route.path === "completeYourProfile" &&
        ["SUBMITTED", "APPROVED"].includes(auth?.user?.status)
      ) {
        return false;
      }
      return true;
    }

    // Check if the route requires permissions and if the user has those permissions
    const hasRequiredPermissions = route.requiredAccess
      ? Object.entries(route.requiredAccess).every(([permission, required]) => {
          const userPermission = userPermissions?.find(
            (perm) => perm?.module === route.module
          );
          return userPermission
            ? userPermission[permission] === required
            : false;
        })
      : true; // If no requiredAccess, treat it as always having access

    // Check if the route module is included in userModules or is an empty module
    const hasModuleAccess =
      userModules?.includes(route.module) || route.module === "";

    // Combine both conditions to filter the routes
    if (hasModuleAccess && hasRequiredPermissions) {
      return { route, ...route.childs };
    }
  }); */

  const filteredRoutes = routes.filter((route) => {
    // Allow routes with empty module (full access)
    if (route.module === "" && !route.childs) {
      // A submitted profile can still be opened, read-only; only approval retires it.
      if (route.path === "completeYourProfile" && auth?.user?.status === "APPROVED") {
        return false;
      }
      return true;
    }

    // A route may accept more than one module (OR semantics) via `modules`;
    // otherwise it falls back to the single `module`.
    const routeModules = route.modules || [route.module];

    // Check if the route requires permissions and if the user has those permissions
    // on any of the acceptable modules.
    const hasRequiredPermissions = route.requiredAccess
      ? routeModules.some((mod) =>
          Object.entries(route.requiredAccess).every(([permission, required]) => {
            const userPermission = userPermissions?.find((perm) => perm?.module === mod);
            return userPermission ? userPermission[permission] === required : false;
          }),
        )
      : true; // If no requiredAccess, treat it as always having access

    // Check if any acceptable module is included in userModules or is an empty module
    const hasModuleAccess = routeModules.some((mod) => userModules?.includes(mod) || mod === "");

    // If the route has module access, filter child routes as well
    if (hasModuleAccess && route.childs) {
      // Filter the child routes
      route.childs = route.childs.filter((child) => {
        const childModules = child.modules || [child.module];
        // If the child route has a module, check if the user has access
        const hasChildModuleAccess = childModules.some(
          (mod) => userModules?.includes(mod) || mod === "",
        );

        // Check if child route requires specific permissions
        const hasChildPermissions = child.requiredAccess
          ? childModules.some((mod) =>
              Object.entries(child.requiredAccess).every(([permission, required]) => {
                const userPermission = userPermissions?.find((perm) => perm?.module === mod);
                return userPermission ? userPermission[permission] === required : false;
              }),
            )
          : true;

        return hasChildModuleAccess && hasChildPermissions;
      });
    }

    // Combine both conditions to filter the routes
    if (hasModuleAccess && hasRequiredPermissions) {
      return { route, ...route.childs };
    }
  });

  const filteredCustomFullPages = routes.filter(
    (route) =>
      route.isCustomFullPage &&
      !(route.path === "completeYourProfile" && auth?.user?.status === "APPROVED"),
  );

  return (
    <GoogleMapsProvider>
      <ThemeProvider theme={materialTheme}>
        <Routes>
          <Route path="/" element={<RequireAuth allowedModules={filteredRoutes}></RequireAuth>}>
            {filteredCustomFullPages.map((route) => (
              <Route path={route.path} element={route.component} key={route.path} />
            ))}
          </Route>
          <Route path="/" element={<Layout />}>
            {/* public routes */}
            <Route path="login" element={<LoginPage />} />
            <Route path="forgotPassword" element={<ForgotPasswordPage />} />
            <Route path="resetPassword" element={<ResetPasswordPage />} />
            <Route path="otp" element={<OtpPage />} />
            <Route path="createNewPassword" element={<CreateNewPasswordPage />} />
            <Route path="clientSignup" element={<CompanySignupForm />} />
            <Route path="customerSignup" element={<CustomerSignupForm />} />
            <Route path="getStarted" element={<GetStartedPage />} />
            <Route path="techSignup" element={<TechnicianSignupForm />} />
            <Route path="requestDemo" element={<TechnicianSignupForm />} />
            <Route path="socialCallback" element={<SocialCallbackPage />} />
            <Route path="/" element={<FlightboardPage />} />
            <Route path="not-found" element={<NoUserPage />} />
            <Route path="unauthorized" element={<h1>Unauthorized</h1>} />

            {/* protected routes */}
            <Route element={<RequireAuth allowedModules={filteredRoutes} />}>
              {filteredRoutes.map((route) => (
                <Route key={route.module} path={route.path} element={route.component}>
                  {route.childs &&
                    route.childs
                      .filter(
                        (child) => !child?.roles || child?.roles?.includes(authRole), // Allow if roles not defined or authRole is included
                      )
                      .map((child) => (
                        <Route key={child.path} path={child.path} element={child.component} />
                      ))}

                  {route.tabs &&
                    route.tabs
                      .filter(
                        (tab) => !tab?.roles || tab?.roles?.includes(authRole), // Allow if roles not defined or authRole is included
                      )
                      .map((tab) => (
                        <Route key={tab?.path} path={tab?.path} element={tab?.component} />
                      ))}
                </Route>
              ))}
            </Route>

            {/* catch all — send not-logged-in users to getStarted, otherwise show Unauthorized */}
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <h1 className="flex items-center justify-center relative top-[100px]">
                    Unauthorized
                  </h1>
                ) : (
                  <Navigate to="/getStarted" replace />
                )
              }
            />
          </Route>
        </Routes>
      </ThemeProvider>
    </GoogleMapsProvider>
  );
}

export default App;
